<script >
import ChartingMobileCard from '@/features/charting-next/components/mobile/ChartingMobileCard.vue';
import { ChartingMobileSeriesController, buildChartingMobileSeriesKey } from '@/features/charting-next/services/chartingMobileSeriesController';
import Vue from 'vue';
import { nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  enabled: {
    type: Boolean,
    default: true
  },
  results: {
    type: Array,
    default: () => []
  },
  resolution: {
    type: String,
    default: 'M'
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  emptyMessage: {
    type: String,
    default: ''
  },
  hasMoreResults: {
    type: Boolean,
    default: false
  },
  isLoadingNextPage: {
    type: Boolean,
    default: false
  },
  pageError: {
    type: [Object, String],
    default: null
  },
  canUseFavorites: {
    type: Boolean,
    default: false
  },
  isFavoriteMutating: {
    type: Boolean,
    default: false
  },
  resetKey: {
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const rootRef = ref(null);
  const sentinelRef = ref(null);
  const activeTickers = ref([]);
  const resources = Vue.observable({});
  const nearElements = new Set();
  const observedElements = new Set();
  let cardObserver;
  let sentinelObserver;
  const seriesController = new ChartingMobileSeriesController({
    maxConcurrent: 3,
    maxCacheEntries: 15,
    onChange: (key, state) => Vue.set(resources, key, state)
  });
  const keyFor = stock => buildChartingMobileSeriesKey(stock?.ticker, props.resolution);
  const resourceFor = stock => resources[keyFor(stock)] || seriesController.stateFor(stock?.ticker, props.resolution);
  const isActive = stock => activeTickers.value.includes(stock?.ticker);
  const requestStock = (stock, force = false) => {
    if (!stock?.ticker) return;
    const currentResource = resourceFor(stock);
    if (!force && (currentResource.status === 'loading' || currentResource.status === 'success')) return;
    seriesController.request(stock.ticker, props.resolution, {
      force
    }).catch(() => {});
  };
  const recomputeActiveTickers = () => {
    if (!props.enabled) {
      if (activeTickers.value.length) activeTickers.value = [];
      return;
    }
    const viewportCenter = (window.innerHeight || 800) / 2;
    const candidates = nearElements.size ? [...nearElements] : [...(rootRef.value?.querySelectorAll('[data-feed-key]') || [])].slice(0, 3);
    const tickers = candidates.filter(element => document.contains(element)).sort((left, right) => {
      const leftRect = left.getBoundingClientRect();
      const rightRect = right.getBoundingClientRect();
      return Math.abs(leftRect.top + leftRect.height / 2 - viewportCenter) - Math.abs(rightRect.top + rightRect.height / 2 - viewportCenter);
    }).slice(0, 5).map(element => element.dataset.feedKey).filter(Boolean);
    const nextTickers = [...new Set(tickers)];
    if (nextTickers.length !== activeTickers.value.length || nextTickers.some((ticker, index) => ticker !== activeTickers.value[index])) {
      activeTickers.value = nextTickers;
    }
    activeTickers.value.forEach(ticker => requestStock(props.results.find(stock => stock.ticker === ticker)));
  };
  const syncObservedCards = () => {
    if (!cardObserver || !rootRef.value) return;
    const currentElements = new Set(rootRef.value.querySelectorAll('[data-feed-key]'));
    observedElements.forEach(element => {
      if (!currentElements.has(element)) {
        cardObserver.unobserve(element);
        observedElements.delete(element);
        nearElements.delete(element);
      }
    });
    currentElements.forEach(element => {
      if (!observedElements.has(element)) {
        observedElements.add(element);
        cardObserver.observe(element);
      }
    });
    recomputeActiveTickers();
  };
  const setupObservers = () => {
    if (typeof IntersectionObserver === 'undefined') {
      activeTickers.value = props.results.slice(0, 5).map(stock => stock.ticker);
      activeTickers.value.forEach(ticker => requestStock(props.results.find(stock => stock.ticker === ticker)));
      return;
    }
    cardObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) nearElements.add(entry.target);else nearElements.delete(entry.target);
      });
      recomputeActiveTickers();
    }, {
      root: null,
      rootMargin: '100% 0px',
      threshold: 0.01
    });
    sentinelObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && props.hasMoreResults && !props.isLoadingNextPage) emit('load-more');
    }, {
      root: null,
      rootMargin: '700px 0px',
      threshold: 0
    });
    if (sentinelRef.value) sentinelObserver.observe(sentinelRef.value);
    syncObservedCards();
  };
  watch(() => props.resolution, () => activeTickers.value.forEach(ticker => requestStock(props.results.find(stock => stock.ticker === ticker))));
  watch(() => props.enabled, enabled => {
    if (!enabled) {
      activeTickers.value = [];
      return;
    }
    nextTick(syncObservedCards);
  });
  watch(() => props.resetKey, () => {
    nearElements.clear();
    activeTickers.value = props.results.slice(0, 3).map(stock => stock.ticker);
    nextTick(syncObservedCards);
  });
  watch(() => props.hasMoreResults, () => {
    if (sentinelObserver && sentinelRef.value) {
      sentinelObserver.unobserve(sentinelRef.value);
      sentinelObserver.observe(sentinelRef.value);
    }
  });
  onMounted(() => nextTick(setupObservers));
  onUpdated(() => nextTick(syncObservedCards));
  onBeforeUnmount(() => {
    cardObserver?.disconnect();
    sentinelObserver?.disconnect();
    seriesController.destroy();
  });
  return {
    emit,
    rootRef,
    sentinelRef,
    resourceFor,
    isActive,
    requestStock
  };
};
__sfc_main.components = Object.assign({
  ChartingMobileCard
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <section ref="rootRef" class="charting-mobile-feed" aria-label="Resultados del graficador">
        <div v-if="isLoading" class="charting-mobile-feed__state">
            <ProgressSpinner style="width: 34px; height: 34px" stroke-width="6" />
            <span>Actualizando resultados…</span>
        </div>
        <div v-else-if="!results.length" class="charting-mobile-feed__state">
            {{ emptyMessage || 'No hay valores visibles para la consulta actual.' }}
        </div>
        <template v-else>
            <ChartingMobileCard
                v-for="stock in results"
                :key="stock.id"
                :stock="stock"
                :resource="resourceFor(stock)"
                :is-active="isActive(stock)"
                :resolution="resolution"
                :can-use-favorites="canUseFavorites"
                :is-favorite-mutating="isFavoriteMutating"
                @open-chart="emit('open-chart', $event)"
                @more-information="emit('more-information', $event)"
                @open-favorites="emit('open-favorites', $event)"
                @retry-chart="requestStock($event, true)"
                @select-entry-variant="emit('select-entry-variant', $event)"
            />
        </template>

        <div ref="sentinelRef" class="charting-mobile-feed__sentinel" aria-live="polite">
            <span v-if="isLoadingNextPage">Cargando más valores…</span>
            <button v-else-if="pageError" type="button" @click="emit('retry-page')">Reintentar carga</button>
            <span v-else-if="results.length && !hasMoreResults">Has llegado al final de los resultados.</span>
        </div>
    </section>
</template>

<style scoped>
.charting-mobile-feed { display: flex; min-width: 0; flex-direction: column; gap: 0.85rem; padding: 0.75rem 0.7rem calc(5.5rem + env(safe-area-inset-bottom)); }
.charting-mobile-feed__state { display: flex; min-height: 50dvh; align-items: center; justify-content: center; gap: 0.75rem; border: 1px dashed var(--charting-b-border-color); border-radius: 1rem; color: var(--p-text-muted-color); text-align: center; padding: 1rem; }
.charting-mobile-feed__sentinel { display: flex; min-height: 2.5rem; align-items: center; justify-content: center; color: var(--p-text-muted-color); font-size: 0.76rem; text-align: center; }
.charting-mobile-feed__sentinel button { border: 0; background: transparent; color: var(--brand-primary); font-weight: 800; cursor: pointer; }
</style>
